import {
  ExtendedAutocomplete,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  MIN_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { ApproverColumnFilter } from '@issuemanager/client/features/issues';
import { useInfiniteUsers } from '@issuemanager/client/features/users';
import { useState } from 'react';
import type { GroupedUserDto, MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export type ApproverMultipleSelectorProps = MultipleSelectorProps<
  GroupedUserDto[],
  GroupedUserDto
>;

export function ApproverMultipleSelector(props: ApproverMultipleSelectorProps) {
  const { value, onChange, enabled: _enabled, ...rest } = props;

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState(value ?? []);
  const issueApproversQuery = useInfiniteUsers({
    filter: {
      searchTerm: filterByText,
      isIssueApprover: true,
    },
    enabled: filterByText.length >= MIN_CHARACTERS_TO_SEARCH,
    handleError: true,
  });
  const actionApproversQuery = useInfiniteUsers({
    filter: {
      searchTerm: filterByText,
      isActionApprover: true,
    },
    enabled: filterByText.length >= MIN_CHARACTERS_TO_SEARCH,
    handleError: true,
  });

  const issueApprovers =
    issueApproversQuery.data?.pages.map((c) =>
      c.data.map((d) => ({ ...d, group: ApproverColumnFilter.ISSUE_APPROVER })),
    ) ?? [];

  const actionApprovers =
    actionApproversQuery.data?.pages.map((c) =>
      c.data.map((d) => ({
        ...d,
        group: ApproverColumnFilter.ACTION_APPROVER,
      })),
    ) ?? [];

  const options = mergePaginatedDataAndValue(
    issueApprovers.concat(actionApprovers),
    value ?? [],
    (option) => `${option.id}-${option.group}`,
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    loading: issueApproversQuery.isLoading || actionApproversQuery.isLoading,
    onScrollEnd: () => {
      issueApproversQuery.fetchNextPage();
      actionApproversQuery.fetchNextPage();
    },
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
    isOptionEqualToValue: (option, value) =>
      option.id === value.id && option.group === value.group,
  });

  const sortedOptions = sortOptionsBySelected(
    options,
    partialValue,
    autocompleteProps.isOptionEqualToValue,
  );

  const finalOptions = sortedOptions
    .filter((o) => o.group === ApproverColumnFilter.ISSUE_APPROVER)
    .concat(
      sortedOptions.filter(
        (o) => o.group === ApproverColumnFilter.ACTION_APPROVER,
      ),
    );

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={finalOptions}
      getOptionKey={(option) => `${option.id}-${option.group}`}
      getOptionLabel={(option) => `${option.name} <${option.email}>`}
      groupBy={(option) => option.group}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
      {...rest}
    />
  );
}
